import React from "react";
import { ScheduleGridData, WEEK_DAYS, WEEK_DAY_LABELS } from "../../types/schedule";
import { Colors } from "../../utils/utils.colors";

type Props = {
  data: ScheduleGridData;
  title?: string;
  subtitle?: string;
  compact?: boolean;
};

/** Renders a weekly course grid (promotions × weekdays), matching the faculty timetable layout. */
export const WeeklyScheduleGrid: React.FC<Props> = ({ data, title, subtitle, compact }) => {
  const facultyTitle = title || data.facultyName || "";
  const weekTitle = subtitle || data.weekLabel || "";

  return (
    <div className={`weekly-schedule${compact ? " weekly-schedule--compact" : ""}`} data-testid="weekly-schedule-grid">
      {(facultyTitle || weekTitle) && (
        <header className="weekly-schedule__header">
          {facultyTitle && <h2 className="weekly-schedule__faculty">{facultyTitle}</h2>}
          {weekTitle && <p className="weekly-schedule__week">{weekTitle.toUpperCase()}</p>}
          <hr className="weekly-schedule__rule" />
        </header>
      )}
      <div className="weekly-schedule__scroll">
        <table className="weekly-schedule__table">
          <thead>
            <tr>
              <th className="weekly-schedule__th weekly-schedule__th--promo">PROMOTIONS</th>
              {WEEK_DAYS.map((d) => (
                <th key={d} className="weekly-schedule__th">{WEEK_DAY_LABELS[d]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, idx) => (
              <tr key={`${row.promotion}-${idx}`}>
                <td className="weekly-schedule__td weekly-schedule__td--promo">{row.promotion}</td>
                {WEEK_DAYS.map((d) => {
                  const cell = (row.days[d] || "").trim();
                  const isExam = /examen/i.test(cell);
                  return (
                    <td
                      key={d}
                      className={`weekly-schedule__td${isExam ? " weekly-schedule__td--exam" : ""}`}
                    >
                      {cell ? (
                        <span dangerouslySetInnerHTML={{ __html: formatCell(cell) }} />
                      ) : (
                        <span className="weekly-schedule__empty">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`
        .weekly-schedule { font-family: "Times New Roman", Times, Georgia, serif; color: ${Colors.darkColor}; }
        .weekly-schedule__header { text-align: center; margin-bottom: 1rem; }
        .weekly-schedule__faculty { font-size: 1.35rem; font-weight: 700; margin: 0 0 0.35rem; color: ${Colors.darkColor}; }
        .weekly-schedule__week { font-size: 0.95rem; font-weight: 700; letter-spacing: 0.04em; margin: 0; }
        .weekly-schedule__rule { border: none; border-top: 2px solid ${Colors.darkColor}; margin: 0.75rem auto 0; max-width: 100%; }
        .weekly-schedule__scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .weekly-schedule__table { width: 100%; border-collapse: collapse; min-width: 720px; font-size: 0.82rem; }
        .weekly-schedule__th, .weekly-schedule__td {
          border: 1px solid #222;
          padding: 0.45rem 0.35rem;
          text-align: center;
          vertical-align: middle;
          line-height: 1.35;
        }
        .weekly-schedule__th {
          background: #f8f9fb;
          font-weight: 700;
          font-size: 0.72rem;
          letter-spacing: 0.03em;
        }
        .weekly-schedule__th--promo, .weekly-schedule__td--promo {
          font-weight: 700;
          text-align: left;
          padding-left: 0.6rem;
          white-space: nowrap;
          background: #fafafa;
          min-width: 88px;
        }
        .weekly-schedule__td--exam { font-weight: 700; }
        .weekly-schedule__empty { color: #bbb; }
        .weekly-schedule--compact .weekly-schedule__table { font-size: 0.75rem; min-width: 600px; }
        .weekly-schedule--compact .weekly-schedule__faculty { font-size: 1.1rem; }
      `}</style>
    </div>
  );
};

function formatCell(text: string): string {
  if (text.includes("<")) return text;
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}
